import React, { useState, useEffect } from 'react';

const FilleulSignupForm = ({ token }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    password: '',
    password_confirmation: '',
    phone: '',
    address: '',
    birth_date: '',
    terms_accepted: false
  });
  const [sponsorship, setSponsorship] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [existingUser, setExistingUser] = useState(false);

  useEffect(() => {
    fetchSponsorshipInfo();
  }, [token]);

  const fetchSponsorshipInfo = async () => {
    try {
      const response = await fetch(`/api/v1/sponsorships/verify_filleul_token?token=${token}`, {
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSponsorship(data.data.sponsorship);
        setExistingUser(data.data.sponsorship.existing_user || false);
        setFormData(prev => ({
          ...prev,
          email: data.data.sponsorship.filleul_email
        }));
      } else {
        setError(data.message || 'Token invalide ou expiré');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!existingUser) {
      // Validation côté client pour les nouveaux utilisateurs uniquement
      if (!formData.first_name.trim() || !formData.last_name.trim()) {
        setError('Le prénom et le nom sont requis');
        setLoading(false);
        return;
      }

      if (formData.password.length < 6) {
        setError('Le mot de passe doit contenir au moins 6 caractères');
        setLoading(false);
        return;
      }

      if (formData.password !== formData.password_confirmation) {
        setError('Les mots de passe ne correspondent pas');
        setLoading(false);
        return;
      }

      if (!formData.terms_accepted) {
        setError('Vous devez accepter les conditions d\'utilisation');
        setLoading(false);
        return;
      }
    }

    try {
      const body = existingUser
        ? { token: token }
        : {
            token: token,
            user: {
              first_name: formData.first_name,
              last_name: formData.last_name,
              password: formData.password,
              password_confirmation: formData.password_confirmation,
              phone: formData.phone,
              address: formData.address,
              birth_date: formData.birth_date
            }
          };

      const response = await fetch('/api/v1/sponsorships/complete_signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setSuccessMessage(existingUser
          ? 'Votre niveau d\'adhésion a été mis à jour ! Vous pouvez vous connecter.'
          : 'Votre compte a été créé avec succès. Vous allez être redirigé...');
        setTimeout(() => {
          window.location.href = '/login';
        }, 3000);
      } else {
        setError(data.message || 'Erreur lors de l\'inscription');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const getLevelLabel = (level) => {
    const labels = {
      sympathisant: 'Adhérent Sympathisant',
      participant: 'Adhérent Participant',
      actif: 'Adhérent Actif'
    };
    return labels[level] || level;
  };

  const pageStyle = {
    minHeight: '100vh',
    background: '#f7eef2',
    paddingTop: '120px',
    paddingBottom: '80px',
    fontFamily: "'Poppins', sans-serif",
  };

  const cardStyle = {
    background: '#ffffff',
    borderRadius: '16px',
    border: '1px solid rgba(124, 40, 55, 0.15)',
    boxShadow: '0 4px 20px rgba(124, 40, 55, 0.08)',
    overflow: 'hidden',
  };

  const gradientStyle = {
    background: 'linear-gradient(135deg, #7c2837 0%, #943143 50%, #a8384e 100%)',
  };

  const inputStyle = {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #e8d0d6',
    fontSize: '14px',
    width: '100%',
    fontFamily: "'Poppins', sans-serif",
    transition: 'border-color 0.2s ease',
  };

  const labelStyle = {
    color: '#2c0e16',
    fontWeight: '500',
    fontSize: '14px',
    marginBottom: '6px',
  };

  if (initialLoading) {
    return (
      <div style={pageStyle}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-7 col-md-9">
              <div className="text-center">
                <div className="spinner-border" style={{ color: '#943143' }} role="status">
                  <span className="visually-hidden">Vérification...</span>
                </div>
                <p className="mt-3" style={{ color: '#6c4a53' }}>Vérification de votre invitation...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !sponsorship) {
    return (
      <div style={pageStyle}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-7 col-md-9">
              <div style={{ ...cardStyle, padding: '48px 32px', textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                <h4 style={{ color: '#7c2837', fontWeight: '600', marginBottom: '12px' }}>Invitation invalide</h4>
                <p style={{ color: '#6c4a53', marginBottom: '24px' }}>{error}</p>
                <a
                  href="/"
                  style={{
                    ...gradientStyle,
                    color: '#ffffff',
                    border: 'none',
                    padding: '12px 32px',
                    borderRadius: '50px',
                    fontWeight: '600',
                    fontSize: '15px',
                    textDecoration: 'none',
                    display: 'inline-block',
                    boxShadow: '0 4px 16px rgba(124, 40, 55, 0.3)',
                  }}
                >
                  Retour à l'accueil
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={pageStyle}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-7 col-md-9">
              <div style={{ ...cardStyle, padding: '48px 32px', textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
                <h4 style={{ color: '#2c0e16', fontWeight: '600', marginBottom: '12px' }}>
                  {existingUser ? 'Niveau mis à jour !' : 'Inscription réussie !'}
                </h4>
                <p style={{ color: '#6c4a53', marginBottom: '24px' }}>
                  {successMessage || 'Vous allez être redirigé vers la page de connexion...'}
                </p>
                <div className="spinner-border spinner-border-sm" style={{ color: '#943143' }} role="status">
                  <span className="visually-hidden">Redirection...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-7 col-md-9">
            {/* En-tête avec informations de parrainage */}
            {sponsorship && (
              <div style={{ ...cardStyle, marginBottom: '24px' }}>
                <div
                  style={{
                    ...gradientStyle,
                    padding: '32px 24px',
                    textAlign: 'center',
                    color: '#ffffff',
                  }}
                >
                  <h3 style={{ fontWeight: '700', marginBottom: '8px', fontSize: '1.5rem' }}>Bienvenue dans Bouillon de Copains !</h3>
                  <p style={{ margin: 0, opacity: 0.9, fontSize: '0.95rem' }}>
                    Vous avez été parrainé pour rejoindre notre association
                  </p>
                </div>
                <div style={{ padding: '24px', background: '#fdf8f9' }}>
                  <div className="row text-center">
                    <div className="col-md-4">
                      <h6 style={{ color: '#7c2837', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Niveau d'adhésion</h6>
                      <p style={{ color: '#2c0e16', fontWeight: '500', marginBottom: 0 }}>{getLevelLabel(sponsorship.requested_level)}</p>
                    </div>
                    <div className="col-md-4">
                      <h6 style={{ color: '#7c2837', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Parrainé par</h6>
                      <p style={{ color: '#2c0e16', fontWeight: '500', marginBottom: 0 }}>{sponsorship.initiator_name}</p>
                    </div>
                    <div className="col-md-4">
                      <h6 style={{ color: '#7c2837', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Soutiens reçus</h6>
                      <p style={{ color: '#2c0e16', fontWeight: '500', marginBottom: 0 }}>{sponsorship.supporters_count} parrains</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Formulaire d'inscription */}
            <div style={cardStyle}>
              <div style={{ padding: '24px 28px', borderBottom: '1px solid rgba(124, 40, 55, 0.1)' }}>
                <h4 style={{ color: '#2c0e16', fontWeight: '600', margin: 0 }}>
                  {existingUser ? 'Confirmer votre nouveau niveau' : 'Finaliser votre inscription'}
                </h4>
              </div>
              <div style={{ padding: '28px' }}>
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              {existingUser ? (
                /* Formulaire simplifié pour utilisateur existant */
                <div>
                  <div style={{ textAlign: 'center', padding: '20px 0 28px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
                    <p style={{ color: '#2c0e16', fontSize: '16px', lineHeight: '1.6', marginBottom: '8px' }}>
                      Vous avez déjà un compte avec l'adresse <strong>{sponsorship?.filleul_email}</strong>.
                    </p>
                    <p style={{ color: '#6c4a53', fontSize: '15px', lineHeight: '1.6' }}>
                      Cliquez sur le bouton ci-dessous pour activer votre nouveau niveau d'adhésion : <strong style={{ color: '#7c2837' }}>{getLevelLabel(sponsorship?.requested_level)}</strong>.
                    </p>
                  </div>
                  <div className="d-grid">
                    <button
                      type="button"
                      onClick={handleSubmit}
                      style={{
                        ...gradientStyle,
                        color: '#ffffff',
                        border: 'none',
                        padding: '14px 32px',
                        borderRadius: '50px',
                        fontWeight: '600',
                        fontSize: '1rem',
                        fontFamily: "'Poppins', sans-serif",
                        boxShadow: '0 8px 24px rgba(124, 40, 55, 0.3)',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.7 : 1,
                        transition: 'all 0.3s ease',
                        width: '100%',
                      }}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Mise à jour en cours...
                        </>
                      ) : (
                        'Confirmer mon nouveau niveau'
                      )}
                    </button>
                  </div>
                </div>
              ) : (
              <form onSubmit={handleSubmit}>
                {/* Email (en lecture seule) */}
                <div className="mb-3">
                  <label htmlFor="email" style={labelStyle}>
                    Adresse email
                  </label>
                  <input
                    type="email"
                    style={{ ...inputStyle, backgroundColor: '#f7eef2', color: '#6c4a53' }}
                    id="email"
                    value={sponsorship?.filleul_email || ''}
                    disabled
                  />
                </div>

                {/* Prénom et Nom */}
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="first_name" style={labelStyle}>
                        Prénom <span style={{ color: '#a8384e' }}>*</span>
                      </label>
                      <input
                        type="text"
                        style={inputStyle}
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="last_name" style={labelStyle}>
                        Nom <span style={{ color: '#a8384e' }}>*</span>
                      </label>
                      <input
                        type="text"
                        style={inputStyle}
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Mot de passe */}
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="password" style={labelStyle}>
                        Mot de passe <span style={{ color: '#a8384e' }}>*</span>
                      </label>
                      <input
                        type="password"
                        style={inputStyle}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        minLength="6"
                        required
                      />
                      <small style={{ color: '#9e6575', fontSize: '12px' }}>
                        Minimum 6 caractères
                      </small>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="password_confirmation" style={labelStyle}>
                        Confirmer le mot de passe <span style={{ color: '#a8384e' }}>*</span>
                      </label>
                      <input
                        type="password"
                        style={inputStyle}
                        id="password_confirmation"
                        name="password_confirmation"
                        value={formData.password_confirmation}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Téléphone */}
                <div className="mb-3">
                  <label htmlFor="phone" style={labelStyle}>
                    Numéro de téléphone
                  </label>
                  <input
                    type="tel"
                    style={inputStyle}
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>

                {/* Adresse */}
                <div className="mb-3">
                  <label htmlFor="address" style={labelStyle}>
                    Adresse complète
                  </label>
                  <textarea
                    style={{ ...inputStyle, resize: 'vertical' }}
                    id="address"
                    name="address"
                    rows="3"
                    value={formData.address}
                    onChange={handleInputChange}
                  />
                </div>

                {/* Date de naissance */}
                <div className="mb-3">
                  <label htmlFor="birth_date" style={labelStyle}>
                    Date de naissance
                  </label>
                  <input
                    type="date"
                    style={inputStyle}
                    id="birth_date"
                    name="birth_date"
                    value={formData.birth_date}
                    onChange={handleInputChange}
                  />
                </div>

                {/* Conditions d'utilisation */}
                <div className="mb-4">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="terms_accepted"
                      name="terms_accepted"
                      checked={formData.terms_accepted}
                      onChange={handleInputChange}
                      required
                    />
                    <label className="form-check-label" htmlFor="terms_accepted" style={{ color: '#2c0e16', fontSize: '14px' }}>
                      J'accepte les <a href="/terms" target="_blank" style={{ color: '#943143' }}>conditions d'utilisation</a> et 
                      la <a href="/privacy" target="_blank" style={{ color: '#943143' }}>politique de confidentialité</a> de l'association
                      <span style={{ color: '#a8384e' }}> *</span>
                    </label>
                  </div>
                </div>

                {/* Bouton de soumission */}
                <div className="d-grid">
                  <button
                    type="submit"
                    style={{
                      ...gradientStyle,
                      color: '#ffffff',
                      border: 'none',
                      padding: '14px 32px',
                      borderRadius: '50px',
                      fontWeight: '600',
                      fontSize: '1rem',
                      fontFamily: "'Poppins', sans-serif",
                      boxShadow: '0 8px 24px rgba(124, 40, 55, 0.3)',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1,
                      transition: 'all 0.3s ease',
                      width: '100%',
                    }}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Création de votre compte...
                      </>
                    ) : (
                      <>
                        Créer mon compte
                      </>
                    )}
                  </button>
                </div>
              </form>
              )}
            </div>
          </div>

            {/* Note de bas de page */}
            <div className="text-center mt-4">
              <small style={{ color: '#9e6575', fontSize: '13px' }}>
                {existingUser
                  ? `En confirmant, votre niveau sera mis à jour vers ${getLevelLabel(sponsorship?.requested_level)}.`
                  : `En créant votre compte, vous rejoignez officiellement l'association Bouillon de Copains en tant qu'${getLevelLabel(sponsorship?.requested_level)}.`
                }
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilleulSignupForm;